/**
 * Color Palette - AutoCollect Design System
 *
 * @module shared/theme/palette
 *
 * Based on the existing AutoCollect UI design with:
 * - Dark theme as primary (modern, professional)
 * - Light theme as alternative
 * - Accent colors for status indicators
 */

// ============================================================================
// Color Tokens
// ============================================================================

export const colors = {
    // Primary Brand Colors
    blue: {
        50: '#eff6ff',
        100: '#dbeafe',
        200: '#bfdbfe',
        300: '#93c5fd',
        400: '#60a5fa',
        500: '#3b82f6', // Primary accent
        600: '#2563eb',
        700: '#1d4ed8',
        800: '#1e40af',
        900: '#1e3a8a',
    },

    // Success / Payment Colors
    green: {
        50: '#f0fdf4',
        100: '#dcfce7',
        200: '#bbf7d0',
        300: '#86efac',
        400: '#4ade80',
        500: '#22c55e', // Success
        600: '#16a34a',
        700: '#15803d',
        800: '#166534',
        900: '#14532d',
    },

    // Warning / PTP Colors
    amber: {
        50: '#fffbeb',
        100: '#fef3c7',
        200: '#fde68a',
        300: '#fcd34d',
        400: '#fbbf24',
        500: '#f59e0b', // Warning
        600: '#d97706',
        700: '#b45309',
        800: '#92400e',
        900: '#78350f',
    },

    // Error / Overdue Colors
    red: {
        50: '#fef2f2',
        100: '#fee2e2',
        200: '#fecaca',
        300: '#fca5a5',
        400: '#f87171',
        500: '#ef4444', // Error
        600: '#dc2626',
        700: '#b91c1c',
        800: '#991b1b',
        900: '#7f1d1d',
    },

    // Secondary Accent Colors
    purple: {
        50: '#faf5ff',
        100: '#f3e8ff',
        200: '#e9d5ff',
        300: '#d8b4fe',
        400: '#c084fc',
        500: '#a855f7',
        600: '#9333ea',
        700: '#7e22ce',
        800: '#6b21a8',
        900: '#581c87',
    },

    cyan: {
        50: '#ecfeff',
        100: '#cffafe',
        200: '#a5f3fc',
        300: '#67e8f9',
        400: '#22d3ee',
        500: '#06b6d4',
        600: '#0891b2',
        700: '#0e7490',
        800: '#155e75',
        900: '#164e63',
    },

    emerald: {
        50: '#ecfdf5',
        100: '#d1fae5',
        200: '#a7f3d0',
        300: '#6ee7b7',
        400: '#34d399',
        500: '#10b981',
        600: '#059669',
        700: '#047857',
        800: '#065f46',
        900: '#064e3b',
    },

    // Neutral Gray Scale
    gray: {
        50: '#f8f9fa',
        100: '#f1f3f4',
        200: '#e8eaed',
        300: '#dadce0',
        400: '#bdc1c6',
        500: '#9aa0a6',
        600: '#80868b',
        700: '#5f6368',
        800: '#3c4043',
        900: '#202124',
    },
} as const;

// ============================================================================
// Dark Theme Palette
// ============================================================================

export const darkPalette = {
    mode: 'dark' as const,

    // Backgrounds
    background: {
        default: '#09090a',
        paper: '#0f0f11',
        elevated: '#151518',
        surface: '#1c1c20',
        tableZebra: '#171a22',
        tableSelected: '#1c2b4a',
    },

    // Segment backgrounds (for timeline items)
    segments: {
        system: '#18181b',
        agent: '#1a1a1e',
        hirer: '#141417',
        payment: '#0d1a0f',
        special: '#000000',
        loan: '#0f1419',
    },

    // Borders
    divider: 'rgba(255, 255, 255, 0.05)',
    border: {
        subtle: 'rgba(255, 255, 255, 0.05)',
        medium: 'rgba(255, 255, 255, 0.10)',
        strong: 'rgba(255, 255, 255, 0.20)',
    },

    // Text
    text: {
        primary: '#f5f5f7',
        secondary: '#a1a1a6',
        disabled: '#6e6e73',
    },

    // Primary (Blue)
    primary: {
        main: colors.blue[500],
        light: colors.blue[400],
        dark: colors.blue[600],
        contrastText: '#ffffff',
    },

    // Secondary (Purple)
    secondary: {
        main: colors.purple[500],
        light: colors.purple[400],
        dark: colors.purple[600],
        contrastText: '#ffffff',
    },

    // Status Colors
    success: {
        main: colors.green[500],
        light: colors.green[400],
        dark: colors.green[600],
        contrastText: '#ffffff',
        soft: 'rgba(34, 197, 94, 0.12)',
    },

    warning: {
        main: colors.amber[500],
        light: colors.amber[400],
        dark: colors.amber[600],
        contrastText: '#000000',
        soft: 'rgba(245, 158, 11, 0.12)',
    },

    error: {
        main: colors.red[500],
        light: colors.red[400],
        dark: colors.red[600],
        contrastText: '#ffffff',
        soft: 'rgba(239, 68, 68, 0.12)',
    },

    info: {
        main: colors.cyan[500],
        light: colors.cyan[400],
        dark: colors.cyan[600],
        contrastText: '#ffffff',
        soft: 'rgba(6, 182, 212, 0.12)',
    },

    // Action states
    action: {
        active: 'rgba(255, 255, 255, 0.56)',
        hover: 'rgba(255, 255, 255, 0.08)',
        selected: 'rgba(255, 255, 255, 0.16)',
        disabled: 'rgba(255, 255, 255, 0.3)',
        disabledBackground: 'rgba(255, 255, 255, 0.12)',
        focus: 'rgba(255, 255, 255, 0.12)',
    },
};

// ============================================================================
// Light Theme Palette
// ============================================================================

export const lightPalette = {
    mode: 'light' as const,

    // Backgrounds
    background: {
        default: '#f8f9fa',
        paper: '#ffffff',
        elevated: '#e8eaed',
        surface: '#f1f3f4',
        tableZebra: '#f7f8fa',
        tableSelected: '#e7f0ff',
    },

    // Segment backgrounds
    segments: {
        system: '#f5f5f5',
        agent: '#e8f0fe',
        hirer: '#f0f4f9',
        payment: '#e6f4ea',
        special: '#fce8e6',
        loan: '#e3f2fd',
    },

    // Borders
    divider: 'rgba(0, 0, 0, 0.06)',
    border: {
        subtle: 'rgba(0, 0, 0, 0.06)',
        medium: 'rgba(0, 0, 0, 0.12)',
        strong: 'rgba(0, 0, 0, 0.23)',
    },

    // Text
    text: {
        primary: '#202124',
        secondary: '#5f6368',
        disabled: '#9aa0a6',
    },

    // Primary (Blue)
    primary: {
        main: colors.blue[600],
        light: colors.blue[400],
        dark: colors.blue[700],
        contrastText: '#ffffff',
    },

    // Secondary (Purple)
    secondary: {
        main: colors.purple[600],
        light: colors.purple[400],
        dark: colors.purple[700],
        contrastText: '#ffffff',
    },

    // Status Colors
    success: {
        main: colors.green[600],
        light: colors.green[400],
        dark: colors.green[700],
        contrastText: '#ffffff',
        soft: 'rgba(22, 163, 74, 0.12)',
    },

    warning: {
        main: colors.amber[600],
        light: colors.amber[400],
        dark: colors.amber[700],
        contrastText: '#000000',
        soft: 'rgba(217, 119, 6, 0.12)',
    },

    error: {
        main: colors.red[600],
        light: colors.red[400],
        dark: colors.red[700],
        contrastText: '#ffffff',
        soft: 'rgba(220, 38, 38, 0.12)',
    },

    info: {
        main: colors.cyan[600],
        light: colors.cyan[400],
        dark: colors.cyan[700],
        contrastText: '#ffffff',
        soft: 'rgba(8, 145, 178, 0.12)',
    },

    // Action states
    action: {
        active: 'rgba(0, 0, 0, 0.54)',
        hover: 'rgba(0, 0, 0, 0.04)',
        selected: 'rgba(0, 0, 0, 0.08)',
        disabled: 'rgba(0, 0, 0, 0.26)',
        disabledBackground: 'rgba(0, 0, 0, 0.12)',
        focus: 'rgba(0, 0, 0, 0.12)',
    },
};

